import { isMultiPart } from '../api/downloads';
import type { DownloadJobControls } from '../hooks/useDownloadJob';
import styles from './DownloadPanel.module.css';

/**
 * Where a download stands: being prepared, ready (with a way to fetch it
 * again), split into parts, or failed. Renders nothing while idle.
 */
export function DownloadPanel({ download }: { download: DownloadJobControls }) {
  const { preparing, job, error, progress } = download;
  if (!preparing && !job && !error) return null;

  const parts = job && isMultiPart(job) ? job.parts! : null;
  const singleReady = job && !parts && job.status === 'COMPLETE';

  return (
    <div className={styles.panel}>
      <div className={styles.body}>
        {preparing && (
          <div className={styles.preparing} role="status" aria-live="polite">
            <span className={styles.spinner} aria-hidden="true" />
            Preparing your download…
          </div>
        )}

        {parts && (
          <section aria-label="Multi-part download">
            <p className={styles.text}>
              This order was large, so your download was split into {job!.part_count ?? parts.length} ZIPs by
              roll.
            </p>
            {progress && (
              <p className={styles.progress} role="status" aria-live="polite">
                Downloading part {progress.current} of {progress.total}…
              </p>
            )}
            <ul className={styles.parts}>
              {parts.map((part) => (
                <li key={part.part_no} className={styles.part}>
                  <span className={styles.partLabel}>{part.label}</span>
                  {part.status === 'COMPLETE' && (
                    <button
                      type="button"
                      className={styles.primary}
                      onClick={() => download.downloadPart(part)}
                      aria-label={`Download ${part.label}`}
                    >
                      Download
                    </button>
                  )}
                  {part.status === 'FAILED' && (
                    <>
                      <span className={styles.failed}>Failed</span>
                      <button
                        type="button"
                        className={styles.secondary}
                        onClick={() => download.retryPart(part)}
                        disabled={preparing}
                        aria-label={`Retry ${part.label}`}
                      >
                        Retry
                      </button>
                    </>
                  )}
                  {(part.status === 'PENDING' || part.status === 'PROCESSING') && (
                    <span className={styles.pending}>Preparing…</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Kept after the automatic download so one the browser blocked or
            lost can be fetched again without rebuilding the ZIP. */}
        {singleReady && (
          <section aria-label="Download ready" className={styles.ready}>
            <p className={styles.text}>
              Your download has started. If it didn't begin, or you need it again, use Download again.
            </p>
            <button type="button" className={styles.primary} onClick={download.downloadAgain}>
              Download again
            </button>
          </section>
        )}

        {error && !preparing && (
          <div className={styles.error} role="alert">
            <p className={styles.errorText}>{error}</p>
            {!job && (
              <button
                type="button"
                className={styles.secondary}
                onClick={download.retry}
                aria-label="Retry download"
              >
                Retry
              </button>
            )}
          </div>
        )}
      </div>

      {!preparing && (
        <button type="button" className={styles.dismiss} onClick={download.dismiss} aria-label="Dismiss download">
          ×
        </button>
      )}
    </div>
  );
}
